import { addLayer, addSlide, newId, touchContentUpdatedAt } from "../model/surfDeck.js";
import { applyLayoutToSlide } from "../layout/applyLayout.js";
import { outlineLayoutForSection, outlineVisualToSlideRef, placeholderLayerForVisual, sectionToLayoutContents, visualBriefFromSection, } from "./outlineSlide.js";
import { sectionTextSubject } from "./types.js";
import { tableLayerFromData } from "../editor/tableLayer.js";
import { chartLayerFromData } from "../chart/chartLayer.js";
import { graphicLayerFromData } from "../graphic/graphicLayer.js";
import { shouldAutoGenerateVisualWidgets } from "../services/composedBackgroundService.js";
import { assignCompositionToSection } from "../style/compositionAssign.js";
import { applyCompositionToSlide } from "../layout/compositionLayout.js";
/** Create seed slides from rich outline sections (F2-rev, F21 grid layouts). */
export function deckFromOutlineSections(deck, sections, presentationType) {
    let next = touchContentUpdatedAt({
        ...deck,
        slides: [],
        outline: sections.map((s) => ({ ...s })),
        theme: {
            ...deck.theme,
            presentationType: presentationType?.trim() || deck.theme.presentationType,
        },
    });
    for (const section of sections) {
        const layoutId = outlineLayoutForSection(section);
        next = addSlide(next, layoutId);
        const slideIndex = next.slides.length - 1;
        const slideId = next.slides[slideIndex].id;
        const contents = sectionToLayoutContents(section);
        let slide = applyLayoutToSlide(next.slides[slideIndex], layoutId, contents, {
            theme: next.theme,
            autoArrange: true,
        });
        const compositionKind = assignCompositionToSection(section, slideIndex);
        slide = applyCompositionToSlide({
            ...slide,
            speakerNotes: section.notes,
            outlineRole: section.role,
            layoutHint: section.layoutHint ?? layoutId,
            outlineVisual: outlineVisualToSlideRef(section.visual),
            visualBrief: visualBriefFromSection(section),
            outlineSectionId: section.id,
        }, compositionKind, next.theme);
        next = touchContentUpdatedAt({
            ...next,
            slides: next.slides.map((s, i) => (i === slideIndex ? slide : s)),
        });
        const autoWidgets = shouldAutoGenerateVisualWidgets(next.theme);
        if (autoWidgets && section.visual?.kind === "table" && section.visual.table) {
            next = addLayer(next, slideId, tableLayerFromData(section.visual.table, { z: 40 }));
        }
        if (autoWidgets && section.visual?.kind === "chart" && section.visual.chart) {
            next = addLayer(next, slideId, chartLayerFromData(section.visual.chart, { z: 41 }));
        }
        if (autoWidgets && section.visual?.kind === "graphic" && section.visual.graphic) {
            next = addLayer(next, slideId, graphicLayerFromData(section.visual.graphic, { z: 42 }));
        }
        if (autoWidgets && section.visual?.kind === "image") {
            const imageLayer = next.slides.find((s) => s.id === slideId)?.layers.find((l) => l.type === "image");
            if (imageLayer) {
                next = touchContentUpdatedAt({
                    ...next,
                    slides: next.slides.map((s) => s.id === slideId
                        ? {
                            ...s,
                            layers: s.layers.map((l) => l.id === imageLayer.id
                                ? {
                                    ...l,
                                    style: {
                                        ...l.style,
                                        imageSubject: section.visual?.imageSubject ?? sectionTextSubject(section),
                                    },
                                }
                                : l),
                        }
                        : s),
                });
            }
        }
        const placeholder = autoWidgets &&
            section.visual &&
            section.visual.kind !== "table" &&
            section.visual.kind !== "chart" &&
            section.visual.kind !== "graphic" &&
            section.visual.kind !== "image"
            ? placeholderLayerForVisual(section.visual, 43)
            : null;
        if (placeholder) {
            next = {
                ...touchContentUpdatedAt(next),
                slides: next.slides.map((s) => s.id === slideId ? { ...s, layers: [...s.layers, { ...placeholder, id: newId() }] } : s),
            };
        }
    }
    return next;
}
